/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

export type ConstraintKey = typeof constraintKeys[number];

/**
 * Server-request entity shape for an individual scheduling constraint.
 * Matches backend SchedulingConstraintRequestEntity: { enabled: boolean, constraintValue?: number }.
 */
export interface SchedulingConstraint {
    /**
     * Indicates whether the constraint is enabled.
     */
    enabled?: boolean;
    /**
     * The value of the constraint.
     */
    constraintValue?: number | null;
}

/**
 * Scheduling constraint rules for a team.
 */
export interface SchedulingConstraints {
    /** Minimum required rest period between any two consecutive shifts (in hours). Prevents "clopening" scenarios. */
    minRestPeriodBetweenShiftsInHours?: SchedulingConstraint;

    /** Maximum number of hours a single calendar day may contain across all scheduled shifts for a member. */
    maxDailyHours?: SchedulingConstraint;

    /** Maximum number of scheduled hours per member in a scheduling week. */
    maxWeeklyHours?: SchedulingConstraint;

    /** Maximum number of days a member can be scheduled to work within a week. */
    maxDaysWorkedPerWeek?: SchedulingConstraint;

    /** Maximum number of consecutive days a member can be scheduled before a day off is required. */
    maxConsecutiveDaysWorked?: SchedulingConstraint;

    /** Minimum number of consecutive hours off required within a scheduling week. */
    minConsecutiveHoursOffPerWeek?: SchedulingConstraint;
}

/**
 * Maps constraint keys to their display information for UI rendering.
 */
export const constraintDisplayMap: Record<ConstraintKey,
    {
        /**
         * The key used for localization.
         */
        labelKey: string;
        /**
         * The unit of measurement (days or hours).
         */
        unit: "hours" | "days"
    }> = {
    maxDailyHours: { labelKey: "maxDailyHours", unit: "hours" },
    maxWeeklyHours: { labelKey: "maxWeeklyHours", unit: "hours" },
    minRestPeriodBetweenShiftsInHours: { labelKey: "minimumRestPeriod", unit: "hours" },
    maxConsecutiveDaysWorked: { labelKey: "maxConsecutiveDaysWorked", unit: "days" },
    maxDaysWorkedPerWeek: { labelKey: "maxDaysWorkedPerWeek", unit: "days" },
    minConsecutiveHoursOffPerWeek: { labelKey: "minConsecutiveHoursOffPerWeek", unit: "hours" }
};

/**
 * Runtime list of constraint keys (iterable, used by UI or logic that needs to iterate constraints).
 * Use `ConstraintKey` for type-safe keys.
 */
export const constraintKeys = [
    'maxDailyHours',
    'maxWeeklyHours',
    'minRestPeriodBetweenShiftsInHours',
    'maxConsecutiveDaysWorked',
    'maxDaysWorkedPerWeek',
    'minConsecutiveHoursOffPerWeek'
] as const;
